import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { azn, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Sprout, TrendingUp, Wallet, Loader2, Sparkles } from "lucide-react";

export default function Marketplace() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [duration, setDuration] = useState(30);
  const [buying, setBuying] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/trees");
      setTrees(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openBuy = (tree) => { setSelected(tree); setDuration(30); };
  const option = selected?.options.find((o) => o.duration_days === duration);

  const confirmBuy = async () => {
    if (!selected) return;
    if ((user?.balance ?? 0) < selected.price) {
      toast.error("Balans kifayət etmir. Cüzdandan balans artırın.");
      setSelected(null);
      navigate("/wallet");
      return;
    }
    setBuying(true);
    try {
      await api.post("/investments", { tree_id: selected.tree_id, duration_days: duration });
      toast.success(`${selected.name} alındı! Gəlir toplanmağa başladı.`);
      setSelected(null);
      await refresh();
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBuying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Ağac Bazarı</h1>
            <p className="text-muted-foreground mt-1">Ağac seçin, müddət təyin edin və gəlir qazanın.</p>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary">
            <Wallet className="w-4 h-4 text-primary" />
            <span className="text-sm">Balans: <b className="font-heading">{azn(user?.balance)}</b></span>
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-96 rounded-2xl" />)}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {trees.map((tree) => {
              const premium = tree.type === "percentage";
              const best = tree.options[tree.options.length - 1];
              return (
                <div
                  key={tree.tree_id}
                  className={`rounded-2xl overflow-hidden border bg-card group transition-transform hover:-translate-y-1 ${premium ? "border-accent/50 shadow-[0_0_30px_-12px_hsl(var(--accent))]" : "border-border"}`}
                  data-testid={`tree-card-${tree.tree_id}`}
                >
                  <div className="h-44 overflow-hidden relative">
                    <img src={tree.image} alt={tree.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    {premium && (
                      <Badge className="absolute top-3 right-3 bg-accent text-accent-foreground border-0"><Sparkles className="w-3 h-3 mr-1" /> Premium</Badge>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-semibold">{tree.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1 min-h-[40px]">{tree.description}</p>
                    <div className="mt-4 flex items-end justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Qiymət</p>
                        <p className="font-heading text-2xl font-bold">{azn(tree.price)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end"><TrendingUp className="w-3 h-3" /> 30 gündə</p>
                        <p className="font-heading text-lg font-bold text-primary">+{azn(best.total_income)}</p>
                      </div>
                    </div>
                    <Button onClick={() => openBuy(tree)} className="w-full rounded-full mt-4" data-testid={`buy-btn-${tree.tree_id}`}>
                      <Sprout className="w-4 h-4 mr-2" /> Al
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="buy-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{selected?.name} al</DialogTitle>
            <DialogDescription>Müddət seçin. Gəlir gün ərzində toplanacaq.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-3 my-2">
            {selected?.options.map((o) => (
              <button
                key={o.duration_days}
                onClick={() => setDuration(o.duration_days)}
                data-testid={`duration-${o.duration_days}`}
                className={`p-4 rounded-xl border text-center transition-colors ${duration === o.duration_days ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border hover:bg-muted"}`}
              >
                <p className="font-heading text-xl font-bold">{o.duration_days}</p>
                <p className="text-xs text-muted-foreground">gün</p>
              </button>
            ))}
          </div>

          {option && (
            <div className="rounded-xl bg-secondary/50 p-4 space-y-2 text-sm" data-testid="buy-summary">
              <div className="flex justify-between"><span className="text-muted-foreground">Qiymət</span><b>{azn(selected.price)}</b></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Gündəlik gəlir</span><b className="text-primary">{azn(option.daily_income)}</b></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Ümumi gəlir ({duration} gün)</span><b className="text-primary">+{azn(option.total_income)}</b></div>
              <div className="flex justify-between border-t border-border pt-2"><span className="text-muted-foreground">Müddət sonu geri alacağınız</span><b>{azn(selected.price + option.total_income)}</b></div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setSelected(null)} data-testid="cancel-buy-btn">Ləğv et</Button>
            <Button className="rounded-full" onClick={confirmBuy} disabled={buying} data-testid="confirm-buy-btn">
              {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : `${azn(selected?.price)} ödə və al`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
